<template>
  <div class="scheduled-task-list">
    <schedule-header
      :label="rangeLabel"
      :has-previous="occurrencePage.hasPrevious"
      :has-next="occurrencePage.hasNext"
      :is-loading="isLoading"
      previous-title="Previous events"
      next-title="Next events"
      @previous="goToPage(page - 1)"
      @next="goToPage(page + 1)"
      @today="goToToday"
      @add-task="$emit('add-task')"
    />

    <loading-placeholder v-if="isLoading && !hasLoaded" :lines="10" />
    <div
      v-else-if="listDays.length === 0"
      class="scheduled-task-list__empty"
    >
      {{ emptyMessage }}
    </div>
    <template v-for="listDay in listDays" :key="listDay.key">
      <div
        v-if="listDay.monthDivider"
        class="month-divider"
      >
        {{ listDay.monthDivider }}
      </div>
      <div
        :class="{ 'is-past': listDay.isPast, 'is-today': listDay.isToday }"
        class="list-day"
      >
        <div class="list-day__date">
          <span class="day-number">{{ listDay.day }}</span>
          <span class="day-meta">{{ listDay.label }}</span>
        </div>
        <div class="list-day__tasks">
          <div
            v-for="scheduledTask in listDay.scheduledTasks"
            :key="scheduledTask.id"
            class="list-task"
            @click="$emit('task-click', scheduledTask, listDay.date)"
          >
            <span :class="statusClass(scheduledTask)" class="status-dot" />
            <span class="task-name">{{ scheduledTask.name }}</span>
            <span class="task-title">{{ scheduledTask.taskTitle }}</span>
            <span
              v-if="scheduledTask.recurrence"
              :title="recurrenceLabel(scheduledTask)"
              class="task-recurrence"
            >
              <i class="ddi ddi-refresh" />{{ recurrenceLabel(scheduledTask) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import ScheduleHeader from './schedule-header.vue'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import {
  IScheduledTask,
  IScheduledTaskOccurrencePage,
} from '@/api/types/response.types'

interface IListDay {
  key: string
  date: Date
  day: number
  label: string
  monthDivider: string | null
  isPast: boolean
  isToday: boolean
  scheduledTasks: IScheduledTask[]
}

const frequencyNames: Record<number, { single: string; unit: string }> = {
  3: { single: 'Daily', unit: 'day' },
  4: { single: 'Weekly', unit: 'week' },
  5: { single: 'Monthly', unit: 'month' },
  6: { single: 'Yearly', unit: 'year' },
}

// Monthly "on the ___ weekday" positions; 0 means the same day of the month
const occurrenceNames: Record<number, string> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'fifth',
  [-1]: 'last',
  [-2]: 'second to last',
  [-3]: 'third to last',
  [-4]: 'fourth to last',
  [-5]: 'fifth to last',
}

function toDateKey(date: Date): string {
  const month = `0${date.getMonth() + 1}`.slice(-2)
  const day = `0${date.getDate()}`.slice(-2)
  return `${date.getFullYear()}-${month}-${day}`
}

// Occurrence dates are date-only; read the yyyy-mm-dd part so the time zone can't shift the day
function fromDateKey(value: string): Date {
  const [year, month, day] = value
    .slice(0, 10)
    .split('-')
    .map(Number)
  return new Date(year, month - 1, day)
}

function monthYearLabel(date: Date, month: 'short' | 'long'): string {
  return `${date.toLocaleString('default', { month })} ${date.getFullYear()}`
}

export default defineComponent({
  name: 'ScheduledTaskList',

  components: {
    ScheduleHeader,
  },

  props: {
    projectId: {
      type: Number,
      required: true,
    },
  },

  emits: ['add-task', 'task-click'],

  data() {
    return {
      // Page 0 starts on this date, earlier events are on negative pages
      anchorDate: toDateKey(new Date()),
      page: 0,
      occurrencePage: {
        page: 0,
        hasPrevious: false,
        hasNext: false,
        occurrences: [],
      } as IScheduledTaskOccurrencePage,
      isLoading: false,
      hasLoaded: false,
      requestCount: 0,
    }
  },

  computed: {
    emptyMessage(): string {
      return this.page < 0 ? 'No earlier events.' : 'No upcoming events.'
    },
    listDays(): IListDay[] {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const listDays: IListDay[] = []
      for (const occurrence of this.occurrencePage.occurrences) {
        const key = occurrence.date.slice(0, 10)
        const previous = listDays[listDays.length - 1]
        if (previous && previous.key === key) {
          previous.scheduledTasks.push(occurrence.scheduledTask)
          continue
        }

        const date = fromDateKey(key)
        const monthName = date
          .toLocaleString('default', { month: 'short' })
          .toUpperCase()
        const weekday = date
          .toLocaleString('default', { weekday: 'short' })
          .toUpperCase()
        const startsMonth =
          previous &&
          (previous.date.getMonth() !== date.getMonth() ||
            previous.date.getFullYear() !== date.getFullYear())
        listDays.push({
          key,
          date,
          day: date.getDate(),
          label: `${monthName}, ${weekday}`,
          monthDivider: startsMonth ? monthYearLabel(date, 'long') : null,
          isPast: date < today,
          isToday: date.getTime() === today.getTime(),
          scheduledTasks: [occurrence.scheduledTask],
        })
      }
      return listDays
    },
    rangeLabel(): string {
      if (this.listDays.length === 0) {
        return monthYearLabel(fromDateKey(this.anchorDate), 'short')
      }
      const first = monthYearLabel(this.listDays[0].date, 'short')
      const last = monthYearLabel(
        this.listDays[this.listDays.length - 1].date,
        'short',
      )
      return first === last ? first : `${first} - ${last}`
    },
  },

  created() {
    this.fetchPage()
  },

  methods: {
    async fetchPage() {
      const requestCount = ++this.requestCount
      this.isLoading = true
      try {
        const occurrencePage = await InternalProjectsAPI.get_ScheduledTasksGetOccurrences(
          this.projectId,
          this.anchorDate,
          this.page,
        )
        // Ignore stale responses when paging quickly
        if (requestCount === this.requestCount) {
          this.occurrencePage = occurrencePage
          this.hasLoaded = true
        }
      } catch (error) {
        this.$error.emit(error)
      } finally {
        if (requestCount === this.requestCount) {
          this.isLoading = false
        }
      }
    },
    goToPage(page: number) {
      this.page = page
      this.fetchPage()
    },
    goToToday() {
      this.anchorDate = toDateKey(new Date())
      this.goToPage(0)
    },
    statusClass(scheduledTask: IScheduledTask): string {
      if (scheduledTask.lastRunDate == null) {
        return 'not-run'
      }
      if (scheduledTask.lastEventDate == null) {
        return 'running'
      }
      return ''
    },
    // e.g. "Every 2 weeks on Thursday, until Dec 31, 2026". The repeat rule is anchored to the event's first date.
    recurrenceLabel(scheduledTask: IScheduledTask): string {
      const recurrence = scheduledTask.recurrence
      const frequency = frequencyNames[recurrence.frequencyType]
      if (!frequency) {
        return ''
      }
      const interval = recurrence.interval
      let label =
        interval > 1 ? `Every ${interval} ${frequency.unit}s` : frequency.single

      const start = fromDateKey(scheduledTask.eventDate)
      const weekday = start.toLocaleString('default', { weekday: 'long' })
      if (recurrence.frequencyType === 4) {
        label += ` on ${weekday}`
      } else if (recurrence.frequencyType === 5) {
        const occurrence = occurrenceNames[recurrence.monthlyByOccurrences]
        label += occurrence
          ? ` on the ${occurrence} ${weekday}`
          : ` on day ${start.getDate()}`
      } else if (recurrence.frequencyType === 6) {
        label += ` on ${start.toLocaleString('default', {
          month: 'short',
          day: 'numeric',
        })}`
      }

      if (recurrence.count) {
        label += `, ${recurrence.count} time${recurrence.count > 1 ? 's' : ''}`
      } else if (recurrence.until) {
        label += `, until ${fromDateKey(recurrence.until).toLocaleString(
          'default',
          { month: 'short', day: 'numeric', year: 'numeric' },
        )}`
      }
      return label
    },
  },
})
</script>

<style lang="scss">
.scheduled-task-list {
  background-color: var(--color-background-primary);
  color: var(--color-text-secondary);

  &__empty {
    padding: 40px 20px;
    text-align: center;
    color: var(--color-text-third);
  }

  .month-divider {
    padding: 16px 20px 6px 20px;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-text-third);
    border-bottom: 1px solid #dde3ea;
  }

  .list-day {
    display: flex;
    padding: 10px 0;
    border-bottom: 1px solid #dde3ea;

    &__date {
      display: flex;
      align-items: center;
      align-self: flex-start;
      flex-shrink: 0;
      width: 140px;
      padding-left: 20px;
      height: 32px;

      @include media-breakpoint-down(sm) {
        flex-direction: column;
        align-items: flex-start;
        width: 70px;
        padding-left: 10px;
        height: auto;
      }

      .day-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 32px;
        height: 32px;
        margin-right: 6px;
        font-size: 20px;
        font-weight: 500;
      }

      .day-meta {
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.5px;
        white-space: nowrap;
      }
    }

    &__tasks {
      flex-grow: 1;
      min-width: 0;
    }

    &.is-past {
      .list-day__date,
      .list-task {
        opacity: 0.6;
      }
    }

    &.is-today .day-number {
      border-radius: 50%;
      color: $color--white;
      background-color: $projects--theme-color;
    }
  }

  .list-task {
    display: flex;
    align-items: center;
    min-height: 32px;
    padding: 0 20px 0 16px;
    font-size: 14px;
    cursor: pointer;
    @include flex-row-horizontal-spacing(12px);

    &:hover {
      background-color: var(--color-background-hover);
    }

    .status-dot {
      flex-shrink: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: var(--color-text-secondary);

      &.not-run {
        background-color: darkgoldenrod;
      }

      &.running {
        background-color: green;
      }
    }

    .task-name {
      font-weight: 500;
      @include single-line-ellipsis;
    }

    .task-title {
      flex: 1;
      color: var(--color-text-third);
      @include single-line-ellipsis;
    }

    // Longer now, so it can shrink (full text in the tooltip) rather than squeeze the name
    .task-recurrence {
      flex-shrink: 1;
      min-width: 0;
      @include single-line-ellipsis;
      font-size: 12px;
      color: var(--color-text-third);

      .ddi {
        margin-right: 4px;
        font-size: 11px;
      }
    }

    // Room is tight, so the repeat description goes on its own line under the name
    @include media-breakpoint-down(sm) {
      flex-wrap: wrap;
      padding-top: 6px;
      padding-bottom: 6px;

      .task-title {
        display: none;
      }

      .task-name {
        flex: 1;
        margin-right: 0;
      }

      .task-recurrence {
        flex-basis: 100%;
        padding-left: 22px;
      }
    }
  }
}
</style>
